/**
 * Vera Share - React Query hooks. Portfolio pattern: a key factory rooted at
 * one namespace plus a blunt whole-module invalidator on mutation settle (the
 * file list is small; precision invalidation is not worth the drift).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './share';

export const shareKeys = {
  all: ['share'] as const,
  meta: () => [...shareKeys.all, 'meta'] as const,
  myFiles: () => [...shareKeys.all, 'my-files'] as const,
  file: (slug: string) => [...shareKeys.all, 'file', slug] as const,
  published: () => [...shareKeys.all, 'published'] as const,
  recipients: (fileId: string) => [...shareKeys.all, 'recipients', fileId] as const,
};

function useShareInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: shareKeys.all });
}

// ── Queries ──

export function useShareMeta() {
  return useQuery({
    queryKey: shareKeys.meta(),
    queryFn: api.fetchMeta,
    staleTime: 5 * 60_000,
  });
}

export function useMyFiles() {
  return useQuery({ queryKey: shareKeys.myFiles(), queryFn: api.fetchMyFiles });
}

export function useShareFile(slug: string | undefined) {
  return useQuery({
    queryKey: shareKeys.file(slug ?? ''),
    queryFn: () => api.fetchFile(slug!),
    enabled: !!slug,
    // A revoked recipient must stop seeing the page promptly, and a retry loop
    // on a deliberate 403/404 is noise.
    retry: false,
  });
}

export function usePublishedFiles(enabled: boolean) {
  return useQuery({
    queryKey: shareKeys.published(),
    queryFn: api.fetchPublished,
    enabled,
  });
}

export function useRecipients(fileId: string | undefined) {
  return useQuery({
    queryKey: shareKeys.recipients(fileId ?? ''),
    queryFn: () => api.fetchRecipients(fileId!),
    enabled: !!fileId,
  });
}

// ── Mutations ──

export function usePublishFile() {
  const invalidate = useShareInvalidator();
  return useMutation({ mutationFn: api.publishFile, onSettled: invalidate });
}

export function usePatchFile() {
  const invalidate = useShareInvalidator();
  return useMutation({
    mutationFn: ({ fileId, patch }: { fileId: string; patch: api.FilePatch }) =>
      api.patchFile(fileId, patch),
    onSettled: invalidate,
  });
}

export function useReplaceContent() {
  const invalidate = useShareInvalidator();
  return useMutation({
    mutationFn: ({ fileId, html }: { fileId: string; html: string }) =>
      api.replaceContent(fileId, html),
    onSettled: invalidate,
  });
}

export function useDeleteFile() {
  const invalidate = useShareInvalidator();
  return useMutation({ mutationFn: api.deleteFile, onSettled: invalidate });
}

export function useAddRecipient() {
  const invalidate = useShareInvalidator();
  return useMutation({
    mutationFn: ({ fileId, personOid, personName }: { fileId: string; personOid: string; personName: string }) =>
      api.addRecipient(fileId, personOid, personName),
    onSettled: invalidate,
  });
}

export function useRemoveRecipient() {
  const invalidate = useShareInvalidator();
  return useMutation({
    mutationFn: ({ fileId, personOid }: { fileId: string; personOid: string }) =>
      api.removeRecipient(fileId, personOid),
    onSettled: invalidate,
  });
}
